// LeaderboardPage — Iter-58.11 (Jun 9, 2026).
//
// Stationary board with multiple category tabs across the top. Each
// tab hits a different `/leaderboard/...` endpoint and renders into
// the same scaffold (rank · subject · score). Period tabs (7d / 30d
// / all-time) apply to whichever category is active.
//
// Six tabs, all per Eric's "g. all of the above":
//   1. Referrers      — patron + profile + waitlist signups
//   2. Producers      — most uploaded videos
//   3. Applauded      — most-applauded individual videos
//   4. Followers      — most-followed channels
//   5. Tippers        — most credits gifted out
//   6. Earners        — most credits received via tips
import React from "react";
import { Link } from "react-router-dom";
import { Trophy, Users, Award, Video, Heart, Coins, Crown, Loader2 } from "lucide-react";
import api from "../lib/apiClient";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const PERIODS = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "all", label: "All-time" },
];

const TABS = [
  { key: "referrers", label: "Referrers", icon: Trophy, endpoint: "/leaderboard/referrals" },
  { key: "producers", label: "Producers", icon: Video, endpoint: "/leaderboard/category/producers" },
  { key: "applauded", label: "Applauded", icon: Heart, endpoint: "/leaderboard/category/applauded-videos" },
  { key: "followers", label: "Followers", icon: Crown, endpoint: "/leaderboard/category/followers" },
  { key: "tippers", label: "Tippers", icon: Coins, endpoint: "/leaderboard/category/tippers" },
  { key: "earners", label: "Earners", icon: Award, endpoint: "/leaderboard/category/earners" },
];

const RANK_COLORS = {
  1: "bg-amber-400/15 border-amber-400/40 text-amber-200",
  2: "bg-gray-300/10 border-gray-400/30 text-gray-200",
  3: "bg-orange-700/15 border-orange-600/30 text-orange-200",
};

// Per-tab copy for the empty state. Keep it specific so the user
// knows *what* nobody has done yet.
const EMPTY_COPY = {
  referrers: "No referrals yet for this period.",
  producers: "No videos uploaded yet for this period.",
  applauded: "No videos have earned applause yet for this period.",
  followers: "No new followers in this period.",
  tippers: "Nobody has tipped credits in this period yet.",
  earners: "Nobody has received tips in this period yet.",
};

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = React.useState("referrers");
  const [period, setPeriod] = React.useState("30d");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const tab = TABS.find((t) => t.key === activeTab) || TABS[0];

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`${tab.endpoint}?period=${period}`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData({ leaders: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, tab.endpoint]);

  const leaders = data?.leaders || [];

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <main className="flex-1 px-4 py-10 sm:py-16 pb-32 sm:pb-16">
        <div className="max-w-3xl mx-auto" data-testid="leaderboard-page">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/10 border border-amber-500/30 mb-3">
              <Trophy className="h-8 w-8 text-amber-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold">GlobalEyes Leaderboards</h1>
            <p className="text-gray-400 mt-2 max-w-xl mx-auto">
              The creators, viewers, and sponsors making this whole thing
              go. Pick a category, pick a window — see who's leading.
            </p>
          </div>

          {/* CATEGORY TABS — sticky stack of six. Horizontal scroll on
              narrow screens so a mobile user can swipe through. */}
          <div className="flex gap-1 overflow-x-auto pb-1 mb-4 -mx-1 px-1 scrollbar-hide" data-testid="leaderboard-category-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  data-testid={`leaderboard-tab-${t.key}`}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 shrink-0 ${
                    active
                      ? "bg-amber-500 text-gray-900"
                      : "bg-gray-900/60 text-gray-400 hover:text-gray-200 border border-gray-800"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Per-tab description / scoring rubric */}
          {activeTab === "referrers" && (
            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3 mb-4 text-xs text-gray-300 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-emerald-400" />
                Waitlist signup =&nbsp;<strong className="text-emerald-200">1 pt</strong>
              </span>
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-amber-300" />
                Signup via your profile =&nbsp;<strong className="text-amber-200">3 pts</strong>
              </span>
              <span className="inline-flex items-center gap-1">
                <Award className="h-3.5 w-3.5 text-amber-400" />
                Patron conversion =&nbsp;<strong className="text-amber-200">10 pts</strong>
              </span>
            </div>
          )}

          {/* Period tabs (7d / 30d / all) */}
          <div className="flex gap-1 bg-gray-900/60 rounded-lg p-1 w-fit mx-auto mb-6">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod(p.value)}
                data-testid={`leaderboard-period-${p.value}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  period === p.value ? "bg-amber-500 text-gray-900" : "text-gray-400 hover:text-gray-200"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Body */}
          {loading ? (
            <div className="text-center py-12 text-gray-500" data-testid="leaderboard-loading">
              <Loader2 className="h-6 w-6 animate-spin inline-block mb-2" />
              <p>Tallying…</p>
            </div>
          ) : leaders.length === 0 ? (
            <div className="text-center py-12 bg-gray-900/40 border border-gray-800 rounded-lg" data-testid="leaderboard-empty">
              <p className="text-gray-300 font-medium mb-1">{EMPTY_COPY[activeTab] || "Nothing to show yet."}</p>
              <p className="text-gray-500 text-sm">
                {user
                  ? <>Be the first — check back as the community grows.</>
                  : <>Sign up to join the rankings.</>}
              </p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="leaderboard-list">
              {leaders.map((row) => {
                const rankClass =
                  RANK_COLORS[row.rank] ||
                  "bg-gray-900/40 border-gray-800 text-gray-300";

                // Subject row varies by tab — videos link to /username?v=id,
                // user rows link to /username. The score+metric label
                // render identically across all six tabs.
                const isVideoRow = activeTab === "applauded" && row.video_id;
                const linkTo = isVideoRow
                  ? `/${row.username}?v=${row.video_id}`
                  : `/${row.username}`;
                const primary = isVideoRow ? row.title : `@${row.username}`;
                const secondary = isVideoRow
                  ? `@${row.username}${row.location?.city ? ` · ${row.location.city}` : ""}`
                  : row.country
                    ? row.country
                    : (activeTab === "referrers"
                       ? `${row.patron_referrals || 0} patron · ${row.profile_signups || 0} profile · ${row.waitlist_referrals || 0} waitlist`
                       : null);

                return (
                  <li
                    key={`${activeTab}-${row.rank}-${row.video_id || row.username}`}
                    data-testid={`leaderboard-row-${row.rank}`}
                    className={`border rounded-lg p-3 flex items-center gap-3 ${rankClass}`}
                  >
                    <span className="text-2xl font-black w-10 text-center">
                      {row.rank}
                    </span>
                    <Link
                      to={linkTo}
                      className="flex-1 min-w-0 hover:underline"
                    >
                      <div className="font-bold truncate">{primary}</div>
                      {secondary && (
                        <div className="text-[11px] opacity-70 truncate">{secondary}</div>
                      )}
                    </Link>
                    <span className="font-bold text-lg flex items-center gap-1 shrink-0">
                      <Trophy className="h-4 w-4 opacity-70" />
                      <span className="tabular-nums">{row.score}</span>
                      {row.metric_label && (
                        <span className="text-[10px] opacity-60 hidden sm:inline ml-1">{row.metric_label}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="text-center text-gray-500 text-xs mt-8" data-testid="leaderboard-cta">
            {user ? (
              <>You&apos;re already in. Share your link — <span className="font-mono text-emerald-300">GlobalEyes.TV/{user.username}</span> — and watch every category climb.</>
            ) : (
              <>Sign up at <span className="font-mono text-emerald-300">GlobalEyes.TV</span> and stake your spot on every board.</>
            )}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LeaderboardPage;
